import useSWRMutation from 'swr/mutation'
import type { Manager, ManagerId } from '@ludus/core'
import { updateManager, type ManagerPatch } from '@/lib/api/managers.ts'
import { MANAGERS_KEY } from '@/shared/hooks/useManagers.ts'

interface UpdateManagerArg {
  id: ManagerId
  patch: ManagerPatch
}

function replaceIn(list: Manager[] | undefined, updated: Manager): Manager[] {
  return (list ?? []).map((m) => (m.id === updated.id ? updated : m))
}

/**
 * Optimistic, because a second switch flipped before the first answer would
 * otherwise send the list as the cache had it before the first flip — and the
 * route replaces the whole list.
 */
export function useUpdateManager() {
  const { trigger, isMutating } = useSWRMutation(
    MANAGERS_KEY,
    (_key: typeof MANAGERS_KEY, { arg }: { arg: UpdateManagerArg }) =>
      updateManager(arg.id, arg.patch),
    {
      populateCache: (updated: Manager, current: Manager[] | undefined) =>
        replaceIn(current, updated),
      revalidate: false,
    },
  )
  const save = (id: ManagerId, patch: ManagerPatch) =>
    trigger(
      { id, patch },
      {
        optimisticData: (current?: Manager[]) => {
          const known = current?.find((m) => m.id === id)
          return known === undefined ? (current ?? []) : replaceIn(current, { ...known, ...patch })
        },
        rollbackOnError: true,
      },
    )
  return { save, isMutating }
}
